import { useEffect, useState } from "react"
import { Button } from "@components/common/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from "@components/common/dialog"
import useTranslator from "@pma/hooks/use-translator"
import { DictType } from "@pma/types"
import { useAuth } from "@pma/stores/auth-store"
import axios from "axios"
import { SERVER_URL, UPDATE_NOTE_ENDPOINT } from "@pma/utils/api-routes"
import { parseCookies } from "nookies"
import { ACCESS_TOKEN, queryClient } from "@pma/utils/constants"
import RichEditorQuill from "../common/rich-editor-quill"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@pma/hooks/use-toast"

function UpdateNote({ message, isOpen, setIsOpen, fromExpandCalling = false }: any) {
  const [value, setValue] = useState(message.note)
  const { translate } = useTranslator()
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const { authConfig } = useAuth()
  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)
  const orgId = authConfig?.last_organization_id
  const organizationUsers = authConfig?.organizationUsers || []

  const mentionUsers = organizationUsers
    ?.filter((user: DictType) => user?.name)
    .map((user: DictType) => {
      return {
        id: user?.id,
        value: user?.name,
        url: user?.user_picture?.url || process.env.PUBLIC_URL + "/user-profile.svg",
      }
    })

  const handleCancel = () => {
    setIsOpen(false)
  }

  const handleChange = (e: any) => {
    const data = JSON.stringify(e.ops)
    setValue(data)
  }

  const updateNoteMutation = useMutation({
    mutationKey: ["update-note"],
    mutationFn: (payload: { note_id: number; organization_id: number; note: { note: string } }) =>
      updateNote(payload),
    onSuccess: (data) => {
      if (fromExpandCalling) {
        queryClient.invalidateQueries({ queryKey: ["get-notes-for-expand"] })
        queryClient.invalidateQueries({ queryKey: ["get-tasks"] })
      } else {
        // queryClient.invalidateQueries({queryKey:['get-tasks']})
        newParams.set("revalidate", "tasks")
        navigate(`?${newParams.toString()}`)
      }
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to update note at this time"),
        variant: "error",
      })
    },
  })

  const updateNote = async (payload: {
    note_id: number
    organization_id: number
    note: { note: string }
  }) => {
    if (!value) {
      return
    }

    const response = await axios.post(SERVER_URL + UPDATE_NOTE_ENDPOINT, payload, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    })
    return response
  }

  const handleSubmit = () => {
    const payload = {
      note_id: message.id,
      organization_id: orgId,
      note: value,
    }

    updateNoteMutation.mutate(payload)
    setIsOpen(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className='w-max max-w-md space-y-4'>
        <DialogTitle>{translate("Update note")}</DialogTitle>
        <div className='max-h-[350px] min-h-[120px] w-[400px] overflow-y-scroll text-sm text-gray-600 shadow focus:ring-0 dark:text-gray-200 md:max-w-[335px]'>
          <RichEditorQuill
            handleChange={handleChange}
            mentionUsers={mentionUsers}
            placeHolder={
              translate('Add new note') + '...\n @' + translate('to mention people')
            }
            id='my-editor'
            value={value.length > 17 ? message.note : ''}
          />
        </div>
        <DialogFooter className='flex flex-row justify-evenly'>
          <DialogClose className='flex'>
            <Button variant='outline' onClick={handleCancel}>
              {translate("Cancel")}{" "}
            </Button>
          </DialogClose>
          <Button variant='default' disabled={!value || value.length===17} onClick={handleSubmit}>
            {translate("Done")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default UpdateNote
